/**
 * TutorialThumb — cinematic, watermarked thumbnail used by Learn tab cards.
 *
 * Renders the resolved image (curated rotation or admin-uploaded URL),
 * overlays:
 *   - bottom gradient for caption legibility
 *   - subtle top-right Carp Cast watermark (low opacity, premium)
 *   - cinematic gold radial vignette
 *   - graceful onError fallback so broken URLs never show a torn icon
 *
 * Aspect: 16/9 (matches the existing `aspect-video` Tailwind utility used by
 * the LessonCard).
 */
import { useState } from 'react';
import { resolveTutorialThumb, TUTORIAL_FALLBACK } from '../lib/tutorialThumbs';

const TutorialThumb = ({
  lesson,
  className = '',
  imgClassName = '',
  showWatermark = true,
  priority = false,
  children,
  testId,
}) => {
  const initial = resolveTutorialThumb(lesson);
  const [src, setSrc] = useState(initial);
  const [errored, setErrored] = useState(false);

  const onError = () => {
    if (errored) return;
    setErrored(true);
    setSrc(TUTORIAL_FALLBACK);
  };

  return (
    <div
      className={`relative aspect-video overflow-hidden bg-[#0b0d0b] ${className}`}
      data-testid={testId}
    >
      <img
        src={src}
        alt={lesson?.title || 'Carp Cast tutorial'}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        onError={onError}
        className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out ${imgClassName}`}
      />

      {/* Bottom gradient — keeps badges + duration chip legible */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

      {/* Cinematic gold radial vignette (top-right corner) */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(200,178,115,0.12),transparent_55%)]" />

      {/* Watermark — uses the existing brand wordmark file */}
      {showWatermark && (
        <div
          className="pointer-events-none absolute top-2.5 right-2.5 z-10 flex items-center gap-1.5 rounded-full bg-black/45 px-2 py-0.5 backdrop-blur-[3px] border border-white/10"
          aria-hidden="true"
          data-testid="tutorial-thumb-watermark"
        >
          <img
            src="/carpcast-icon.png"
            alt=""
            loading="lazy"
            decoding="async"
            className="h-3 w-3 opacity-90"
            draggable={false}
          />
          <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-white/85">
            The Carp Cast
          </span>
        </div>
      )}

      {children}
    </div>
  );
};

export default TutorialThumb;
